import { useState, useRef, useCallback, useEffect } from 'react';
import { envConfig } from '@/config/env';

// ASR WebSocket 消息类型定义
export interface ASRMessage {
  eventType: 'system' | 'message' | 'event';
  type: string;
  [key: string]: unknown;
}

export interface SessionInitMessage extends ASRMessage {
  eventType: 'system';
  type: 'session_init';
  sessionId: string;
  sessionStatus: 'new' | 'resume';
  timestamp: number;
}

export interface AsrTranscriptMessage extends ASRMessage {
  eventType: 'message';
  type: 'asr_transcript';
  text: string;
  isFinal: boolean;
  timestamp: number;
}

export interface AsrStatusMessage extends ASRMessage {
  eventType: 'message';
  type: 'asr_status';
  state: RecordingStatus;
  message: string;
  timestamp: number;
}

export interface AsrErrorMessage extends ASRMessage {
  eventType: 'system';
  type: 'asr_error';
  code: string;
  message: string;
  timestamp: number;
}

export interface ErrorMessage extends ASRMessage {
  eventType: 'system';
  type: 'error';
  code: number;
  message: string;
}
export interface AsrStopMessage extends ASRMessage {
  eventType: 'event';
  type: 'asr_stop';
}

export interface AsrPauseMessage extends ASRMessage {
  eventType: 'event';
  type: 'asr_pause';
}

export interface AsrContinueMessage extends ASRMessage {
  eventType: 'event';
  type: 'asr_continue';
}

export interface PingMessage extends ASRMessage {
  eventType: 'event';
  type: 'ping';
}

export interface PongMessage extends ASRMessage {
  eventType: 'event';
  type: 'pong';
}

export interface ASRConfig {
  /** 面试编号 */
  interviewNumber: number;
  /** 识别结果回调 */
  onResult?: (result: ASRResult) => void;
  /** 错误回调 */
  // onError?: (error: Error) => void;
}

export interface ASRResult {
  text: string;
  isFinal: boolean;
  timestamp: number;
}

export type RecordingStatus = 'closed' | 'started' | 'paused';

export interface UseASRReturn {
  /** asr 服务状态 */
  asrStatus: RecordingStatus;
  /** 是否静音 */
  isMuted: boolean;
  /** 状态消息 */
  stateMessage: string;
  /** 错误 */
  errorCode: string | number | null;
  /** 文本转译状态：true 表示最终结果，false 表示临时结果 */
  isFinal: boolean;
  /** 暂停 asr 转译服务 */
  pause: () => void;
  /** 继续 asr 转译服务 */
  continue: () => void;
  /** 手动重连 */
  reconnect: () => Promise<void>;
}

// 心跳配置
const HEARTBEAT_INTERVAL = 30000; // 30秒发送一次心跳
const HEARTBEAT_TIMEOUT = 60000; // 60秒未收到响应则认为超时
const MAX_RECONNECT_ATTEMPTS = 3; // 最大重连次数

const floatTo16BitPCM = (input: Float32Array) => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output.buffer;
};

/**
 * 实时语音识别 Hook
 * 基于后端 ASR WebSocket 服务
 */
export function useASR(config: ASRConfig): UseASRReturn {
  const [asrStatus, setAsrStatus] = useState<RecordingStatus>('closed');
  const [stateMessage, setStateMessage] = useState('未连接');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isFinal, setIsFinal] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [interviewNumber, setInterviewNumber] = useState<number>();

  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const audioInputRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const heartbeatTimeoutTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const isManualReconnectRef = useRef<boolean>(false);
  const isMutedRef = useRef<boolean>(false);
  const onResultCallbackRef = useRef<((result: ASRResult) => void) | undefined>();

  // 更新回调引用
  useEffect(() => {
    onResultCallbackRef.current = config.onResult;
    setInterviewNumber(config.interviewNumber);
  }, [config.onResult, config.interviewNumber]);

  /**
   * 清理所有定时器
   */
  const clearAllTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (heartbeatTimeoutTimerRef.current) {
      clearTimeout(heartbeatTimeoutTimerRef.current);
      heartbeatTimeoutTimerRef.current = null;
    }
  }, []);

  /**
   * 发送心跳
   */
  const sendHeartbeat = useCallback(() => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const pingMessage: PingMessage = {
        eventType: 'event',
        type: 'ping',
      };
      websocketRef.current.send(JSON.stringify(pingMessage));

      // 设置超时检测
      if (heartbeatTimeoutTimerRef.current) {
        clearTimeout(heartbeatTimeoutTimerRef.current);
      }
      heartbeatTimeoutTimerRef.current = setTimeout(() => {
        // 心跳超时，关闭连接触发重连
        if (websocketRef.current) {
          websocketRef.current.close();
        }
      }, HEARTBEAT_TIMEOUT);
    }
  }, []);

  /**
   * 启动心跳
   */
  const startHeartbeat = useCallback(() => {
    clearAllTimers();
    // 立即发送一次心跳
    sendHeartbeat();
    // 设置定期心跳
    heartbeatTimerRef.current = setInterval(() => {
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }, [sendHeartbeat, clearAllTimers]);

  /**
   * 处理心跳响应
   */
  const handlePong = useCallback(() => {
    // 收到 pong，清除超时计时器
    if (heartbeatTimeoutTimerRef.current) {
      clearTimeout(heartbeatTimeoutTimerRef.current);
      heartbeatTimeoutTimerRef.current = null;
    }
  }, []);

  /**
   * 准备音频流（保留原有预热逻辑）
   */
  const prepareAudioStream = useCallback(async () => {
    if (!audioStreamRef.current) {
      // 打印耗时
      const startTime = Date.now();
      // 1、获取音频输入设备
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = audioStream;
      audioStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      const endTime = Date.now();
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`getUserMedia 耗时: ${endTime - startTime}ms`);
      }
    }
    return audioStreamRef.current;
  }, []);

  /**
   * 停止录音（内部清理函数）
   */
  const cleanup = useCallback(() => {
    // 断开 ScriptProcessor
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }

    // 断开音频输入
    if (audioInputRef.current) {
      audioInputRef.current.disconnect();
      audioInputRef.current = null;
    }

    // 停止音频流（但不释放，保持预热）
    if (audioStreamRef.current) {
      audioStreamRef.current.getAudioTracks().forEach((track) => {
        track.stop();
        // track.enabled = false;
      });
      audioStreamRef.current = null;
    }

    // 关闭 AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    clearAllTimers();
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const stopMessage: AsrStopMessage = {
        eventType: 'event',
        type: 'asr_stop',
      };
      websocketRef.current.send(JSON.stringify(stopMessage));
      websocketRef.current.close();
    }
    websocketRef.current = null;
    setStateMessage('未连接');
    setErrorCode(null);
  }, []);

  /**
   * 连接 WebSocket
   */
  const connect = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      // 如果已经连接，直接返回
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // 清理之前的连接和定时器
      clearAllTimers();
      if (websocketRef.current && websocketRef.current.readyState !== WebSocket.CLOSED) {
        websocketRef.current.close();
      }

      // 构建 WebSocket URL
      const token = window.config?.token;
      if (!token) {
        const err = new Error('未找到认证令牌');
        setStateMessage(err.message);
        reject(err);
        return;
      }

      const wsUrl = `${envConfig.wsBaseUrl}/api/asr?token=${token}&interviewNumber=${interviewNumber}`;
      websocketRef.current = new WebSocket(wsUrl);

      websocketRef.current.onopen = () => {
        setStateMessage('已连接');
        reconnectAttemptsRef.current = 0; // 重置重连次数
        // 连接成功后启动心跳
        startHeartbeat();
        resolve();
      };

      websocketRef.current.onmessage = (event) => {
        try {
          // 处理二进制音频数据（服务端可能返回，但这里主要处理文本消息）
          if (event.data instanceof Blob || event.data instanceof ArrayBuffer) {
            // 忽略二进制数据（如果需要处理，可以在这里添加逻辑）
            return;
          }

          const message = JSON.parse(event.data) as ASRMessage;

          // 收到任何消息都重置心跳超时（表示连接活跃）
          handlePong();

          // 处理会话初始化
          if (message.eventType === 'system' && message.type === 'session_init') {
            setStateMessage(`会话已初始化`);
          }

          // 处理识别结果
          if (message.eventType === 'message' && message.type === 'asr_transcript') {
            const transcriptMessage = message as AsrTranscriptMessage;
            const asrResult: ASRResult = {
              text: transcriptMessage.text,
              isFinal: transcriptMessage.isFinal,
              timestamp: transcriptMessage.timestamp,
            };
            setIsFinal(transcriptMessage.isFinal);
            onResultCallbackRef.current?.(asrResult);
          }

          // 处理 ASR 状态
          if (message.eventType === 'event' && message.type === 'asr_status') {
            const statusMessage = message as AsrStatusMessage;
            setStateMessage(statusMessage.message);
            setAsrStatus(statusMessage.state);
          }

          // 处理 ASR 专用错误
          if (message.eventType === 'system' && message.type === 'asr_error') {
            const errorMessage = message as AsrErrorMessage;
            setErrorCode(errorMessage.code);
            setIsFinal(true);
          }

          // 处理通用错误
          if (message.eventType === 'system' && message.type === 'error') {
            const errorMessage = message as ErrorMessage;
            setStateMessage(errorMessage.message);
            setErrorCode(errorMessage.code + '');
            setIsFinal(true);

            // 认证失败等错误，关闭连接
            if (errorMessage.code === 401 || errorMessage.code === 1008) {
              websocketRef.current?.close(1008, '认证失败');
            }
          }
        } catch (e) {
          // 静默处理解析错误，避免影响用户体验
          // 开发环境下输出错误信息
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.error('解析消息失败:', e);
          }
        }
      };

      websocketRef.current.onerror = () => {
        const err = new Error('WebSocket 连接错误');
        setStateMessage(err.message);
        clearAllTimers();
        reject(err);
      };

      websocketRef.current.onclose = (event) => {
        setStateMessage('未连接');
        clearAllTimers();

        // 如果不是主动关闭，尝试重连
        if (
          event.code !== 1000 && // 正常关闭
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS &&
          !isManualReconnectRef.current
        ) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000); // 指数退避，最多10秒

          setTimeout(() => {
            if (websocketRef.current?.readyState !== WebSocket.OPEN) {
              connect()
                .then(() => {
                  // 重连成功
                })
                .catch(console.error);
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          // 达到最大重连次数
          const err = new Error('连接失败，已重试 3 次');
          setStateMessage(err.message);
        }

        isManualReconnectRef.current = false;
      };
    });
  }, [interviewNumber, clearAllTimers, startHeartbeat, handlePong]);

  /**
   * 开始
   */
  const start = useCallback(async () => {
    try {
      // 前置条件检查
      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        // 如果未连接，先连接
        await connect();
      }

      // 再次检查连接状态
      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket 未连接，请先连接');
      }

      // 1、获取音频输入设备（使用预热的音频流）
      const audioStream = await prepareAudioStream();

      // 2、创建 AudioContext，设置采样率为 16000
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      // 3、创建音频源
      const audioInput = audioContext.createMediaStreamSource(audioStream);
      audioInputRef.current = audioInput;

      // 4、创建 ScriptProcessor，缓冲区大小为 4096
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = scriptProcessor;

      scriptProcessor.onaudioprocess = (event) => {
        // 如果静音，不发送音频数据
        if (isMutedRef.current) {
          return;
        }

        const inputData = event.inputBuffer.getChannelData(0);

        if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
          websocketRef.current.send(floatTo16BitPCM(inputData));
        }
      };

      audioInput.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      setErrorCode(null);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('录音失败，请检查麦克风权限');
      setStateMessage(err.message);
    }
  }, [connect, prepareAudioStream]);

  /**
   * 暂停 asr 转译服务（发送 pause 事件）
   */
  const pauseService = useCallback(() => {
    isMutedRef.current = true;
    setIsMuted(true);
    // 发送 pause 事件
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const pauseMessage: AsrPauseMessage = {
        eventType: 'event',
        type: 'asr_pause',
      };
      websocketRef.current.send(JSON.stringify(pauseMessage));
    }
  }, []);

  const continueService = useCallback(() => {
    isMutedRef.current = false;
    setIsMuted(false);
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const continueMessage: AsrContinueMessage = {
        eventType: 'event',
        type: 'asr_continue',
      };
      websocketRef.current.send(JSON.stringify(continueMessage));
    }
  }, []);

  /**
   * 手动重连
   */
  const reconnect = useCallback(async () => {
    isManualReconnectRef.current = true;
    reconnectAttemptsRef.current = 0; // 重置重连次数
    cleanup();
    await connect();
  }, [connect, cleanup]);

  // 组件挂载时预热音频流
  useEffect(() => {
    prepareAudioStream().catch((err) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('音频预热失败:', err);
      }
    });

    // 组件卸载时清理资源
    return cleanup;
  }, [prepareAudioStream, cleanup]);

  // 当 interviewNumber 有效时，自动连接并开始录音
  useEffect(() => {
    if (interviewNumber) {
      start().catch((err) => {
        setStateMessage(err.message);
      });
    }
  }, [interviewNumber, start]);

  return {
    asrStatus,
    errorCode,
    stateMessage,
    isFinal,
    isMuted,
    pause: pauseService,
    continue: continueService,
    reconnect,
  };
}
